import { LoadingOutlined } from '@ant-design/icons';
import { useTranslate } from '@tolgee/react';
import { Collapse, Spin } from 'antd';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntercom } from 'react-use-intercom';
import styled from 'styled-components';

import { CrossSvg, ExclamationMarkSvg } from '../../../../assets/icons';
import CuripodAIStarsSvg from '../../../../assets/icons/CuripodAIStarsSvg';
import FoldedHandsSVG from '../../../../assets/icons/FoldedHands';
import FolderSvg from '../../../../assets/icons/FolderSvg';
import GoogleDriveSvg from '../../../../assets/icons/GoogleDriveSvg';
import MagicStarsSvg from '../../../../assets/icons/MagicStarsSvg';
import PaperClip from '../../../../assets/icons/Paperclip';
import UploadsSvg from '../../../../assets/icons/UploadsSvg';
import theme from '../../../../assets/theme';
import { LicenseCoarseLevels } from '../../../../hooks/useAccessBadge';
import useAuthGuardPrompt from '../../../../hooks/useAuthGuardPrompt';
import { useBreakPoint } from '../../../../hooks/useBreakPoint';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import useGoogleDrive from '../../../../hooks/useGoogleDrive';
import { useMetrics } from '../../../../hooks/useMetrics';
import usePaywallPrompt from '../../../../hooks/usePaywallPrompt';
import useRouterMatch from '../../../../hooks/useRouterMatch';
import { useUploadGoogleDrive } from '../../../../hooks/useUploadGoogleDrive';
import { useUploadPresentation } from '../../../../hooks/useUploadPresentation';
import { PromptInput } from '../../../../services/backendService/types';
import { GeneratorCoarseLicenseLevel } from '../../../../services/TemplateService/Template.type';
import {
  MAX_LIMIT_LEARNING_OBJECTIVE,
  MAX_LIMIT_PASSAGE,
  MAX_LIMIT_PROMPT,
} from '../../../../utils/aiGeneratorUtils';
import { debounceEventIfNotTrusted } from '../../../../utils/general';
import { isIos } from '../../../../utils/navigator';
import { Input } from '../../../_atoms/_Input';
import { Loader } from '../../../_atoms/_Loader';
import { TextArea } from '../../../_atoms/_TextArea';
import { TextAreaV2 } from '../../../_atoms/_TextArea/TextAreaV2';
import { Button } from '../../../_atoms/Button';
import { useGoogleDrivePicker } from '../../../_atoms/GoogleDrivePicker';
import { CallbackDoc } from '../../../_atoms/GoogleDrivePicker/typeDefs';
import { IconWrapper } from '../../../_atoms/IconWrapper';
import BaseModal from '../../../_molecules/BaseModal';
import PresentationFileSelector, {
  PresentationFileSelectorV2,
} from '../../../_molecules/PresentationFileSelector';
import { FloatingSelect } from '../../../dynamicPages/CreatePage/components/FloatingSelect';
import {
  GeneratorCombobox,
  GeneratorDropdown,
  GeneratorDropdownV2,
  GeneratorMultiSelect,
} from './GeneratorDropdown';
import { UploadPassageInputV2 } from './UploadPassageInputV2';

const InputElement = ({
  promptInput,
  onChange,
  promptInputSuggestions,
  value,
}: {
  promptInput: PromptInput;
  promptInputSuggestions?: PromptInput[];
  onChange: (val: number | string | { filePath: string; fileType: string }) => void;
  value?: string | number | { filePath: string; fileType?: string };
}) => {
  const renderedSuggestion = useSinglePromptinputSuggestion({
    promptInput,
    promptInputSuggestions: promptInputSuggestions,
    value,
  });
  if (promptInput.type === 'shortText') {
    return (
      <Input
        style={{ width: '100%' }}
        type="text"
        value={value as string | undefined}
        label={promptInput.label}
        placeholder={renderedSuggestion as string}
        labelStyle={{ fontWeight: 'bold', color: theme.colors.black }}
        maxLength={promptInput.constraints?.maxLength || MAX_LIMIT_PROMPT}
        error={
          value &&
          (value as string).length >=
            (promptInput.constraints?.maxLength || MAX_LIMIT_PROMPT)
            ? `${(value as string).length}/${promptInput.constraints?.maxLength ||
                MAX_LIMIT_PROMPT}`
            : undefined
        }
        onChange={e => onChange(e.target.value)}
      />
    );
  }
  if (promptInput.type === 'longText') {
    return (
      <TextArea
        style={{ width: '100%' }}
        label={promptInput.label}
        labelStyle={{ fontWeight: 'bold', color: theme.colors.black }}
        rows={3}
        placeholder={renderedSuggestion}
        symbolCount={true}
        maxLength={promptInput.constraints?.maxLength || MAX_LIMIT_LEARNING_OBJECTIVE}
        value={value as string | undefined}
        error={
          value &&
          (value as string).length >=
            (promptInput.constraints?.maxLength || MAX_LIMIT_LEARNING_OBJECTIVE)
            ? `${(value as string).length}/${promptInput.constraints?.maxLength ||
                MAX_LIMIT_LEARNING_OBJECTIVE}`
            : undefined
        }
        textAreaStyled={{ minHeight: '100px' }}
        onChange={e => onChange(e.target.value)}
      />
    );
  }
  if (promptInput.type === 'dropdown') {
    return (
      <GeneratorDropdown
        label={promptInput.label}
        value={value as string | undefined}
        options={promptInput.constraints?.options || []}
        onSelect={e => onChange(e as string | number)}
      ></GeneratorDropdown>
    );
  }
  if (promptInput.type === 'multiSelect') {
    return (
      <GeneratorMultiSelect
        label={promptInput.label}
        value={value as string | undefined}
        options={promptInput.constraints?.options || []}
        onSelect={e => onChange(e)}
        maxSelections={promptInput.constraints?.maxSelections}
      />
    );
  }
  if (promptInput.type === 'combobox') {
    return (
      <GeneratorCombobox
        label={promptInput.label}
        value={value as string | undefined}
        options={promptInput.constraints?.options || []}
        onSelect={e => onChange(e)}
      />
    );
  }

  if (promptInput.type === 'passage') {
    return (
      <TextArea
        style={{ width: '100%' }}
        label={promptInput.label}
        labelStyle={{ fontWeight: 'bold', color: theme.colors.black }}
        rows={3}
        placeholder={renderedSuggestion}
        symbolCount={true}
        maxLength={15000}
        value={(value as { filePath: string; fileType: string }).filePath}
        error={
          value && (value as string).length >= 15000
            ? `${(value as string).length}/15000`
            : undefined
        }
        textAreaStyled={{ minHeight: '100px' }}
        onChange={e => onChange({ filePath: e.target.value, fileType: '' })}
      />
    );
  }
  if (promptInput.type == 'slidesUpload') {
    return (
      <InputFileElement
        onChange={onChange}
        label={promptInput.label}
        value={value as { filePath: string; fileType: string } | undefined}
      />
    );
  }

  return null;
};

const InputElementV2 = ({
  promptInput,
  onChange,
  promptInputSuggestions,
  value,
}: {
  promptInput: PromptInput;
  promptInputSuggestions?: PromptInput[];
  onChange: (val: number | string | { filePath: string; fileType?: string }) => void;
  value?: string | number | { filePath: string; fileType?: string };
}) => {
  const [passageValue, setPassageValue] = useState<
    { filePath: string; fileType?: string } | undefined
  >(value as { filePath: string; fileType: string } | undefined);

  const handleFileUploaded = (uploadedFile: { filePath: string; fileType?: string }) => {
    onChange({ filePath: uploadedFile.filePath, fileType: uploadedFile.fileType });
  };

  const renderedSuggestion = useSinglePromptinputSuggestion({
    promptInput,
    promptInputSuggestions: promptInputSuggestions,
    value,
  });

  if (promptInput.type === 'shortText') {
    return (
      <div>
        <Input
          style={{ width: '100%' }}
          type="text"
          borderLess
          inputStyle={{
            backgroundColor: 'transparent',
            borderRadius: 0,
            color: '#FFF',
            fontFeatureSettings: "'liga' off, 'clig' off",
            fontFamily: 'Nunito',
            fontSize: '24px',
            fontStyle: 'normal',
            fontWeight: 'bold',
            lineHeight: '130%',
          }}
          value={value as string | undefined}
          label={promptInput.label}
          placeholder={renderedSuggestion as string}
          labelStyle={{
            fontSize: '18px',

            color: '#FFF',
            fontFeatureSettings: "'liga' off, 'clig' off",
            fontFamily: 'Nunito',
            fontStyle: 'normal',
            fontWeight: 800,
            lineHeight: '140%',
          }}
          maxLength={promptInput.constraints?.maxLength || MAX_LIMIT_PROMPT}
          error={
            value &&
            (value as string).length >=
              (promptInput.constraints?.maxLength || MAX_LIMIT_PROMPT)
              ? `${(value as string).length}/${promptInput.constraints?.maxLength ||
                  MAX_LIMIT_PROMPT}`
              : undefined
          }
          onChange={e => onChange(e.target.value)}
        />
        <div
          style={{
            height: '5px',
            backgroundColor: 'white',
            margin: '0px 6px',
            borderRadius: '3px',
          }}
        ></div>
      </div>
    );
  }
  if (promptInput.type === 'longText') {
    return (
      <TextAreaV2
        style={{ width: '100%' }}
        label={promptInput.label}
        labelStyle={{
          fontSize: '18px',
          color: '#FFF',
          fontFeatureSettings: "'liga' off, 'clig' off",
          fontFamily: 'Nunito',
          fontStyle: 'normal',
          fontWeight: 800,
          lineHeight: '140%',
        }}
        rows={3}
        placeholder={renderedSuggestion}
        symbolCount={true}
        maxLength={promptInput.constraints?.maxLength || MAX_LIMIT_LEARNING_OBJECTIVE}
        value={value as string | undefined}
        error={
          value &&
          (value as string).length >=
            (promptInput.constraints?.maxLength || MAX_LIMIT_LEARNING_OBJECTIVE)
            ? `${(value as string).length}/${promptInput.constraints?.maxLength ||
                MAX_LIMIT_LEARNING_OBJECTIVE}`
            : undefined
        }
        textAreaStyled={{
          minHeight: '100px',
          backgroundColor: 'transparent',
          color: 'white',
        }}
        onChange={e => onChange(e.target.value)}
      />
    );
  }
  if (promptInput.type === 'dropdown') {
    return (
      <GeneratorDropdownV2
        label={promptInput.label}
        value={value as string | undefined}
        options={promptInput.constraints?.options || []}
        onSelect={e => onChange(e as string | number)}
      ></GeneratorDropdownV2>
    );
  }
  if (promptInput.type === 'multiSelect') {
    return (
      <GeneratorMultiSelect
        label={promptInput.label}
        value={value as string | undefined}
        options={promptInput.constraints?.options || []}
        onSelect={e => onChange(e)}
        maxSelections={promptInput.constraints?.maxSelections}
        variant="v2"
      />
    );
  }
  if (promptInput.type === 'combobox') {
    return (
      <GeneratorCombobox
        label={promptInput.label}
        value={value as string | undefined}
        options={promptInput.constraints?.options || []}
        onSelect={e => onChange(e)}
        variant="v2"
      />
    );
  }

  if (promptInput.type === 'passage') {
    return (
      <UploadPassageInput
        onFileUploaded={handleFileUploaded}
        uploadedFile={value as { filePath: string; fileType: string } | undefined}
        onChange={onChange}
        label={promptInput.label}
        promptInput={promptInput}
        passageValue={passageValue}
        setPassageValue={setPassageValue}
      />
    );
  }
  if (promptInput.type == 'slidesUpload') {
    return (
      <InputFileElementV2
        onChange={onChange}
        label={promptInput.label}
        value={value as { filePath: string; fileType: string } | undefined}
      />
    );
  }

  return null;
};

/** The passage field's props, shared by the v1 and v2 renderings. */
type UploadPassageInputProps = {
  onFileUploaded: (uploadedFile: { filePath: string; fileType?: string }) => void;
  uploadedFile?: { filePath: string; fileType?: string } | undefined;
  onChange: (val: number | string | { filePath: string; fileType?: string }) => void;
  label?: string;
  promptInput: PromptInput;
  passageValue: { filePath: string; fileType?: string | undefined } | undefined;
  setPassageValue: React.Dispatch<
    React.SetStateAction<{ filePath: string; fileType?: string } | undefined>
  >;
  labelStyle?: React.CSSProperties;
  style?: React.CSSProperties;
};

/**
 * The generator's passage field. Behind `upload_review_passage` — the one gate
 * on "bring your own passage" — the paperclip becomes the composer's "Add
 * Materials" dropdown and an uploaded file is parsed into the textarea instead
 * of travelling as a file path; see `UploadPassageInputV2`. Off, this is the
 * pdf/jpeg/png upload it has always been.
 */
export const UploadPassageInput = (props: UploadPassageInputProps) => {
  const isFileParsingV2Enabled = useFeatureFlag('upload_review_passage');

  if (isFileParsingV2Enabled) {
    return <UploadPassageInputV2 {...props} />;
  }
  return <UploadPassageInputV1 {...props} />;
};

const UploadPassageInputV1 = ({
  onFileUploaded,
  uploadedFile,
  onChange,
  label,
  promptInput,
  passageValue,
  setPassageValue,
  labelStyle,
  style,
}: {
  onFileUploaded: (uploadedFile: { filePath: string; fileType?: string }) => void;
  uploadedFile?: { filePath: string; fileType?: string } | undefined;
  onChange: (val: number | string | { filePath: string; fileType?: string }) => void;
  label?: string;
  promptInput: PromptInput;
  passageValue: { filePath: string; fileType?: string | undefined } | undefined;
  setPassageValue: React.Dispatch<
    React.SetStateAction<{ filePath: string; fileType?: string } | undefined>
  >;
  labelStyle?: React.CSSProperties;
  style?: React.CSSProperties;
}) => {
  const [isFileFormatErrorVisible, setIsFileFormatErrorVisible] = useState(false);
  const tenantId = useRouterMatch().tenantId;
  const {
    fileInfo: presentationFileInfo,
    upload: uploadPresentation,
    isUploading: isUploadingPresentation,
    isSuccess: uploadPresentationSuccess,
    isError: isErrorUploadingPresentation,
    reset: resetPresentationUpload,
  } = useUploadPresentation();

  const {
    fileInfo: googleDriveFileInfo,
    upload: uploadGoogleDrive,
    isUploading: isUploadingGoogleDrive,
    isSuccess: uploadGoogleDriveSuccess,
    isError: isErrorUploadingGoogleDrive,
    reset: resetGoogleDriveUpload,
  } = useUploadGoogleDrive(tenantId);
  const metrics = useMetrics();
  const { isReady: googleDriveIsReady, isError: googleDriveIsError } = useGoogleDrive(
    tenantId,
  );
  const [latestUploadMethod, setLatestUploadMethod] = useState<
    'presentation' | 'googleDrive' | undefined
  >();
  const [fileSizeExceeded, setFileSizeExceeded] = useState(false);
  const [presentation, setPresentation] = useState<File | undefined>(undefined);
  const [googleDriveFile, setGoogleDriveFile] = useState<CallbackDoc | undefined>(
    undefined,
  );
  const router = useRouter();
  const { selectedCardId } = router.query;
  const isDesktop = useBreakPoint(700);
  const handleLocalFileSelected = useCallback(
    (file: File) => {
      const fileSizeInMb = file.size * 1e-6;
      metrics.logEvent('Creator.Upload.Passage.Generator.Selected', {
        fileSize: fileSizeInMb,
        fileType: file.type,
      });
      if (fileSizeInMb <= 100) {
        setFileSizeExceeded(false);
        setPresentation(file);
        uploadPresentation(
          { file },
          {
            onError: (error, { file }) => {
              metrics.logEvent('UploadMaterials.Error', {
                selectedCardId: selectedCardId as string,
                fileName: file?.name,
                fileType: file?.type,
                fileSize: file?.size,
                errorMessage: (error as Error)?.message,
                tenantId,
              });
            },
            onSuccess: (_, { file }) => {
              metrics.logEvent('UploadMaterials.Success', {
                selectedCardId: selectedCardId as string,
                fileName: file?.name,
                fileType: file?.type,
                fileSize: file?.size,
                tenantId,
              });
            },
          },
        );
      }

      if (fileSizeInMb > 100) {
        setFileSizeExceeded(true);
      }
    },
    [metrics, uploadPresentation, selectedCardId, tenantId],
  );

  useEffect(() => {
    if (uploadPresentationSuccess && presentationFileInfo && presentation) {
      setPassageValue(undefined);
      onFileUploaded({
        fileType: presentationFileInfo.fileType,
        filePath: presentationFileInfo.filePath,
      });
      resetPresentationUpload();
    }
  }, [
    presentationFileInfo,
    uploadPresentationSuccess,
    resetPresentationUpload,
    onFileUploaded,
    presentation,
    setPassageValue,
  ]);

  const handleGoogleDriveFileSelected = useCallback(
    (file: CallbackDoc) => {
      const fileSizeInMb = file.sizeBytes * 1e-6;
      metrics.logEvent('LessonPage.Upload.GoogleDrive.Generator.Selected', {
        fileSize: fileSizeInMb,
        fileType: file.type,
      });
      if (fileSizeInMb <= 100) {
        setFileSizeExceeded(false);
        setGoogleDriveFile(file);
        uploadGoogleDrive({ fileId: file.id });
      } else {
        setFileSizeExceeded(true);
      }
    },
    [metrics, uploadGoogleDrive],
  );
  const handleClearUploadedFile = () => {
    // Reset local file
    resetPresentationUpload();
    setPresentation(undefined);
    // Reset google drive file
    resetGoogleDriveUpload();
    setGoogleDriveFile(undefined);
    onChange('');
  };

  const { showFilePicker: showGoogleDrivePicker } = useGoogleDrivePicker(
    handleGoogleDriveFileSelected,
    tenantId,
    'DOCS',
    'application/pdf,pdf',
  );

  // Add ref for file input
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Modified showLocalFilePicker function
  const showLocalFilePicker = useCallback(() => {
    if (!fileInputRef.current) {
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.id = 'fileInput';
      fileInput.name = 'myFile';
      fileInput.multiple = false;
      fileInput.accept = isIos ? '*' : '.pdf, .jpeg, .jpg, .png';
      fileInput.style.display = 'none';

      fileInput.onchange = () => {
        const supportedFormats = ['.pdf', '.jpeg', '.jpg', '.png'];

        if (fileInput.files && fileInput.files.length > 0) {
          const fileName = fileInput.files[0].name;
          const fileExtension = fileName.substring(fileName.lastIndexOf('.'));
          const isSupportedFormat = supportedFormats.includes(
            fileExtension.toLowerCase(),
          );
          if (!isSupportedFormat) {
            setIsFileFormatErrorVisible(true);
            return;
          }
          handleLocalFileSelected(fileInput.files[0]);
        }
      };

      fileInput.onclick = () => {
        fileInput.value = '';
      };

      fileInputRef.current = fileInput;
      document.body.appendChild(fileInput);
    }

    fileInputRef.current.click();
  }, [handleLocalFileSelected]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (fileInputRef.current) {
        document.body.removeChild(fileInputRef.current);
        fileInputRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    //So we can correctly set the error messages and other upload properties
    if (isUploadingPresentation) setLatestUploadMethod('presentation');
    if (isUploadingGoogleDrive) setLatestUploadMethod('googleDrive');
  }, [isUploadingPresentation, isUploadingGoogleDrive]);

  // Joint status variables for both upload methods
  const uploadStatus = useMemo(() => {
    const isOngoing =
      (latestUploadMethod === 'presentation' && isUploadingPresentation) ||
      (latestUploadMethod === 'googleDrive' && isUploadingGoogleDrive);

    const isError =
      (latestUploadMethod === 'presentation' && isErrorUploadingPresentation) ||
      (latestUploadMethod === 'googleDrive' && isErrorUploadingGoogleDrive);

    const isSuccess =
      (latestUploadMethod === 'presentation' && uploadPresentationSuccess) ||
      (latestUploadMethod === 'googleDrive' && uploadGoogleDriveSuccess);

    const file = uploadedFile;
    const index = uploadedFile?.filePath?.indexOf('_');
    let fileName = '';
    if (index !== undefined && index !== -1) {
      fileName =
        uploadedFile?.filePath.substring(index + 1) ||
        uploadedFile?.filePath ||
        'My file';
    }

    const isStarted = !!file?.fileType || isOngoing || isError || isSuccess;
    return {
      isOngoing,
      isError,
      isSuccess,
      isStarted,
      file,
      fileName,
    };
  }, [
    isUploadingPresentation,
    isUploadingGoogleDrive,
    isErrorUploadingPresentation,
    isErrorUploadingGoogleDrive,
    uploadPresentationSuccess,
    uploadGoogleDriveSuccess,
    uploadedFile,
    latestUploadMethod,
  ]);

  useEffect(() => {
    if (uploadGoogleDriveSuccess && googleDriveFileInfo && googleDriveFile) {
      onFileUploaded({
        fileType: googleDriveFileInfo.fileType,
        filePath: googleDriveFileInfo.filePath,
      });
      resetGoogleDriveUpload();
    }
  }, [
    googleDriveFileInfo,
    uploadGoogleDriveSuccess,
    resetGoogleDriveUpload,
    onFileUploaded,
    googleDriveFile,
    uploadStatus,
  ]);

  useEffect(() => {
    if (uploadStatus.isError) {
      setIsFileFormatErrorVisible(true);
    }
  }, [uploadStatus.isError]);

  const handleDropdownSelect = (option: string) => {
    if (option === 'local') {
      metrics.logEvent('LessonPage.Upload.Passage.Generator.Clicked');
      showLocalFilePicker();
    }
    if (option === 'googleDrive') {
      metrics.logEvent('LessonPage.Upload.GoogleDrive.Passage.Generator.Clicked');
      showGoogleDrivePicker();
    }
  };

  return (
    <>
      <div style={{ position: 'relative' }}>
        {(!uploadStatus.isStarted || uploadStatus.isError) && (
          <TextAreaV2
            style={{ width: '100%' }}
            label={label ? label : 'Passage'}
            labelStyle={
              !!labelStyle
                ? { ...labelStyle }
                : {
                    fontSize: '18px',
                    color: '#FFF',
                    fontFeatureSettings: "'liga' off, 'clig' off",
                    fontFamily: 'Nunito',
                    fontStyle: 'normal',
                    fontWeight: 800,
                    lineHeight: '140%',
                  }
            }
            rows={3}
            placeholder={'Paste your passage, or upload a file'}
            symbolCount={true}
            maxLength={promptInput.constraints?.maxLength || MAX_LIMIT_PASSAGE}
            value={passageValue?.filePath as string | undefined}
            error={
              passageValue &&
              (passageValue?.filePath as string)?.length >=
                (promptInput.constraints?.maxLength || MAX_LIMIT_PASSAGE)
                ? `${(passageValue?.filePath as string)?.length}/${promptInput.constraints
                    ?.maxLength || MAX_LIMIT_PASSAGE}`
                : undefined
            }
            textAreaStyled={
              !!style
                ? { ...style }
                : {
                    minHeight: '100px',
                    backgroundColor: 'transparent',
                    color: 'white',
                    paddingBottom: '45px',
                  }
            }
            onChange={e => {
              if (passageValue?.fileType) {
                return;
              }
              if (!passageValue?.fileType) {
                setPassageValue({ filePath: e.target.value, fileType: undefined });
                onChange({ filePath: e.target.value, fileType: undefined });
              }
            }}
          />
        )}
        <BaseModal
          open={isFileFormatErrorVisible}
          onCancel={() => setIsFileFormatErrorVisible(false)}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'column',
            }}
          >
            <IconWrapper height="120px" width="120px" style={{ marginTop: 20 }}>
              <FoldedHandsSVG />
            </IconWrapper>

            <ErrorMessage>
              <>
                <ErrorMessage>{'Unsupported file format'}</ErrorMessage>

                <ErrorMessage style={{ fontSize: '18px' }}>
                  {'Please upload a file with .pdf, .jpeg, or .png format.'}
                </ErrorMessage>
              </>
            </ErrorMessage>
            <Button
              styles={{ marginTop: 20 }}
              width="120px"
              onClick={() => setIsFileFormatErrorVisible(false)}
            >
              Ok
            </Button>
          </div>
        </BaseModal>

        {(!uploadStatus.isStarted || uploadStatus.isError) && (
          <SelectContainer>
            <FloatingSelect
              darkMode={true}
              style={{
                width: '100%',
                borderRadius: 5,
                boxShadow: 'none',
                padding: '10px',
              }}
              width={'100%'}
              value={undefined}
              disabled={tenantId === undefined}
              placeholder={
                <IconWrapper
                  width={'20px'}
                  height={'20px'}
                  color={!!style ? theme.colors.black : theme.colors.white}
                >
                  <PaperClip />
                </IconWrapper>
              }
              options={[
                {
                  value: 'local',
                  displayName: 'Upload (.pdf, .jpeg, .png)',
                  icon: <UploadsSvg />,
                },
                {
                  value: 'googleDrive',
                  displayName: 'Google Drive',
                  icon: googleDriveIsError ? (
                    <ExclamationMarkSvg />
                  ) : !googleDriveIsReady ? (
                    <Spin
                      indicator={
                        <LoadingOutlined
                          style={{
                            fontSize: 20,
                            color: theme.colors.orange,
                            marginRight: 10,
                          }}
                          spin
                        />
                      }
                    />
                  ) : (
                    <GoogleDriveSvg />
                  ),
                  disabled: !googleDriveIsReady,
                },
              ]}
              onSelect={s => handleDropdownSelect(s)}
            />
          </SelectContainer>
        )}
        {uploadStatus.isOngoing && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              background: `${theme.colors.white}`,
              borderRadius: 5,
              padding: '9px 15px',
              position: 'relative',
              boxShadow: theme.effects.floatingCreatorElementShadow,
              margin: isDesktop ? '0' : '10px 0',
            }}
            onClick={e => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            <Spin
              indicator={
                <LoadingOutlined
                  style={{
                    fontSize: 20,
                    color: theme.colors.orange,
                    marginRight: 10,
                  }}
                  spin
                />
              }
            />{' '}
            Uploading...
          </div>
        )}
        {uploadStatus?.file?.fileType && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              width: isDesktop ? 200 : '100%',
              background: `${theme.colors.paleBeige}`,
              borderRadius: 5,
              padding: '9px 15px',
              position: 'relative',
              boxShadow: theme.effects.floatingCreatorElementShadow,
              margin: isDesktop ? '0' : '10px 0',
            }}
            onClick={e => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            <IconWrapper height="12px" width="12px" style={{ marginRight: 5 }}>
              <FolderSvg />
            </IconWrapper>
            <p
              style={{
                whiteSpace: 'nowrap',
                maxWidth: isDesktop ? 200 : '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                marginBottom: 0,
                marginLeft: 5,
              }}
            >
              {uploadStatus?.fileName}
            </p>
            <IconWrapper
              height="8px"
              width="8px"
              style={{
                marginBottom: 10,
                boxShadow: theme.effects.magicFloatingShadow,
                borderRadius: 50,
                padding: 5,
                background: theme.colors.greyLight,
                position: 'absolute',
                right: '-8px',
                top: '-8px',
              }}
              onClick={e => {
                e.stopPropagation();
                e.preventDefault();
                handleClearUploadedFile();
              }}
            >
              <CrossSvg />
            </IconWrapper>
          </div>
        )}
      </div>
      {fileSizeExceeded && (
        <div style={{ display: 'inline-block' }}>The file was too large</div>
      )}
      {uploadStatus.isError && (
        <div style={{ color: theme.colors.red }}>Failed to upload file</div>
      )}
    </>
  );
};

const SelectContainer = styled.div`
  position: absolute;
  bottom: 1px;
  left: 1px;
  z-index: 100;
`;

const InputFileElement = ({
  onChange,
  value,
  label,
}: {
  value: { filePath: string; fileType: string } | undefined;
  onChange: (val: number | string | { filePath: string; fileType: string }) => void;
  label: string;
}) => {
  const {
    fileInfo,
    upload: uploadPresentation,
    isUploading: isUploadingFile,
    isSuccess: uploadPresentationSuccess,
    isError: isErrorUploadingFile,
    reset: resetFileUpload,
  } = useUploadPresentation();
  const [fileSizeExceeded, setFileSizeExceeded] = useState(false);

  const [uploadedFile, setUploadedFile] = useState<
    | {
        filePath: string;
        fileType: string;
      }
    | undefined
  >(value);
  const handleFileSelected = useCallback(
    (file: File) => {
      const fileSizeInMb = file.size * 1e-6;
      if (fileSizeInMb <= 100) {
        setFileSizeExceeded(false);
        uploadPresentation({ file });
      }

      if (fileSizeInMb > 100) {
        setFileSizeExceeded(true);
      }
    },
    [uploadPresentation],
  );

  useEffect(() => {
    if (uploadPresentationSuccess && fileInfo) {
      setUploadedFile(fileInfo);
      resetFileUpload();
      onChange(fileInfo);
    }
  }, [fileInfo, onChange, uploadPresentationSuccess, resetFileUpload]);
  return (
    <FileUploadArea
      handleFileSelected={handleFileSelected}
      isUploadingFile={isUploadingFile}
      uploadedFile={uploadedFile}
      setUploadedFile={setUploadedFile}
      isErrorUploadingFile={isErrorUploadingFile}
      resetFileUpload={resetFileUpload}
      fileSizeExceeded={fileSizeExceeded}
      label={label}
    />
  );
};

export const InputFileElementV2 = ({
  onChange,
  value,
  label,
}: {
  value: { filePath: string; fileType: string } | undefined;
  onChange: (val: number | string | { filePath: string; fileType: string }) => void;
  label: string;
}) => {
  const {
    fileInfo,
    upload: uploadPresentation,
    isUploading: isUploadingFile,
    isSuccess: uploadPresentationSuccess,
    isError: isErrorUploadingFile,
    reset: resetFileUpload,
  } = useUploadPresentation();
  const [fileSizeExceeded, setFileSizeExceeded] = useState(false);

  const [uploadedFile, setUploadedFile] = useState<
    | {
        filePath: string;
        fileType: string;
      }
    | undefined
  >(value);
  const handleFileSelected = useCallback(
    (file: File) => {
      const fileSizeInMb = file.size * 1e-6;
      if (fileSizeInMb <= 100) {
        setFileSizeExceeded(false);
        uploadPresentation({ file });
      }

      if (fileSizeInMb > 100) {
        setFileSizeExceeded(true);
      }
    },
    [uploadPresentation],
  );

  useEffect(() => {
    if (uploadPresentationSuccess && fileInfo) {
      setUploadedFile(fileInfo);
      resetFileUpload();
      onChange(fileInfo);
    }
  }, [fileInfo, onChange, uploadPresentationSuccess, resetFileUpload]);
  return (
    <FileUploadAreaV2
      handleFileSelected={handleFileSelected}
      isUploadingFile={isUploadingFile}
      uploadedFile={uploadedFile}
      setUploadedFile={setUploadedFile}
      isErrorUploadingFile={isErrorUploadingFile}
      resetFileUpload={resetFileUpload}
      fileSizeExceeded={fileSizeExceeded}
      label={label}
    />
  );
};

export type GeneratorFormState = {
  [inputId: string]: string | number | { filePath: string; fileType?: string };
};

const generateFormState = (promptInputs: PromptInput[]): GeneratorFormState => {
  return promptInputs.reduce((acc, promptInput) => {
    return {
      ...acc,
      [promptInput.id]: promptInput.defaultValue || '',
    };
  }, {});
};

export const GeneratorForm = ({
  promptInputs,
  handleInputChange,
  handleMultipleInputsChange,
  handleSubmit,
  isLoading,
  isError,
  error,
  formState,
  promptInputSuggestions,
  isPremiumGenerator,
  generatorPlan,
  generatorId,
  upgradeLocation,
  preFillSearch,
  preFillGrade,
}: {
  promptInputs: PromptInput[];
  promptInputSuggestions?: PromptInput[][];
  handleInputChange: (
    inputId: string,
    value: string | number | { filePath: string; fileType: string },
  ) => void;
  handleMultipleInputsChange?: (
    inputsToChange: {
      inputId: string;
      value: string | number | { filePath: string; fileType: string };
    }[],
  ) => void;
  handleSubmit: () => void;
  isLoading?: boolean;
  isError?: boolean;
  error?: any;
  formState?: GeneratorFormState;
  isPremiumGenerator?: boolean | null;
  generatorPlan?: GeneratorCoarseLicenseLevel | null;
  generatorId?: string;
  upgradeLocation?: string;
  preFillSearch?: string;
  preFillGrade?: string;
}) => {
  const [showErrorModal, setShowErrorModal] = useState(false);
  const { showNewMessages } = useIntercom();
  const { userAccessBadge, isReady, promptPaywall, paywallComponent } = usePaywallPrompt({
    startState: { screen: 'schoolPlanPaywall' },
  });
  const { authGuardComponent, showAuthGuard } = useAuthGuardPrompt(
    undefined,
    undefined,
    generatorId,
  );
  const requiredInputs = useMemo(() => promptInputs.filter(p => p.isRequired), [
    promptInputs,
  ]);
  const optionalInputs = useMemo(() => promptInputs.filter(p => !p.isRequired), [
    promptInputs,
  ]);
  const currentPromptInputSuggestions = usePromptInputSuggestions({
    inputSuggestions: promptInputSuggestions,
    formState,
    promptInputs,
  });

  useEffect(() => {
    if (isError) {
      setShowErrorModal(true);
    }
  }, [isError]);

  //Pre fill form with search and or grade
  useEffect(() => {
    if (preFillSearch && !preFillGrade) {
      const topicInput = promptInputs.find(p => p.label.includes('topic'));

      if (topicInput) {
        handleInputChange(topicInput.id, preFillSearch);
      }

      return;
    }
    if (!preFillSearch && preFillGrade) {
      const gradeInput = promptInputs.find(p => p.id.includes('grade'));
      if (gradeInput) {
        handleInputChange(gradeInput.id, preFillGrade);
      }

      return;
    }

    if (preFillSearch && preFillGrade) {
      const topicInput = promptInputs.find(p => p.label.includes('topic'));
      const gradeInput = promptInputs.find(p => p.id.includes('grade'));

      if (topicInput && gradeInput && handleMultipleInputsChange) {
        handleMultipleInputsChange([
          { inputId: topicInput.id, value: preFillSearch },
          { inputId: gradeInput.id, value: preFillGrade },
        ]);
      }
      return;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preFillSearch, preFillGrade]);

  const isFormEnabled = useMemo(() => {
    if (formState) {
      return requiredInputs.every(input => {
        const realValue = formState?.[input.id];
        return realValue && realValue !== '';
      });
    }
    return false;
  }, [requiredInputs, formState]);

  return (
    <FormContainer>
      {paywallComponent}
      {authGuardComponent}
      <BaseModal open={showErrorModal} onCancel={() => setShowErrorModal(false)}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
          }}
        >
          <IconWrapper height="120px" width="120px" style={{ marginTop: 20 }}>
            <FoldedHandsSVG />
          </IconWrapper>

          <ErrorMessage style={{ fontWeight: 800 }}>
            Uh-oh! Something went wrong on our end
          </ErrorMessage>

          <ErrorMessage style={{ fontSize: '16px' }}>
            {error?.customMessage ||
              'Please try again or contact support if the issue persists'}
          </ErrorMessage>
          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <Button width="120px" onClick={() => setShowErrorModal(false)}>
              Ok
            </Button>
            <Button
              width="120px"
              onClick={() => {
                const errorMessage = error?.customMessage || 'Unknown error occurred';
                showNewMessages(
                  `I'm having issues with lesson generation and need help.\n\nError: ${errorMessage}`,
                );
                setShowErrorModal(false);
              }}
            >
              Get Help
            </Button>
          </div>
        </div>
      </BaseModal>
      <h3>Customize this lesson</h3>
      <GridContainer>
        {requiredInputs?.map(promptInput => (
          <InputElement
            key={promptInput.id}
            promptInput={promptInput}
            promptInputSuggestions={currentPromptInputSuggestions}
            value={formState?.[promptInput.id]}
            onChange={val => handleInputChange(promptInput.id, val)}
          />
        ))}

        {optionalInputs.length > 0 && (
          <OptionalInputCollapse ghost style={{ width: '100%' }}>
            <OptionalInputCollapsePanel
              header={'Optional inputs'}
              key="optional"
              style={{ color: theme.colors.black, fontWeight: 'bold' }}
            >
              <GridContainer>
                {optionalInputs?.map(promptInput => (
                  <InputElement
                    key={promptInput.id}
                    value={formState?.[promptInput.id]}
                    promptInputSuggestions={currentPromptInputSuggestions}
                    promptInput={promptInput}
                    onChange={val => handleInputChange(promptInput.id, val)}
                  />
                ))}
              </GridContainer>
            </OptionalInputCollapsePanel>
          </OptionalInputCollapse>
        )}
      </GridContainer>
      {isReady &&
      generatorPlan === GeneratorCoarseLicenseLevel.enterprise &&
      userAccessBadge?.license.coarseLevel !== LicenseCoarseLevels.enterprise ? (
        <CreateButton
          icon={<MagicStarsSvg />}
          type="primary"
          loading={isLoading}
          onClick={e => {
            debounceEventIfNotTrusted({
              cb: () => {
                if (!isReady) {
                  showAuthGuard();
                  return;
                }
                if (
                  generatorPlan === GeneratorCoarseLicenseLevel.enterprise &&
                  userAccessBadge?.license.coarseLevel !== LicenseCoarseLevels.enterprise
                ) {
                  promptPaywall({
                    label: 'Upgrade account',
                    description: `Generator form: ${generatorId}`,
                  });
                  return;
                }
              },
              isTrusted: e.isTrusted,
            });
          }}
          disabled={!isFormEnabled}
        >
          Customize now
        </CreateButton>
      ) : (
        <CreateButton
          icon={<MagicStarsSvg />}
          type="primary"
          loading={isLoading}
          onClick={e => {
            debounceEventIfNotTrusted({
              cb: () => {
                if (!isReady) {
                  showAuthGuard();
                  return;
                }
                if (
                  isPremiumGenerator &&
                  userAccessBadge?.license.coarseLevel !== LicenseCoarseLevels.premium &&
                  userAccessBadge?.license.coarseLevel !== LicenseCoarseLevels.enterprise
                ) {
                  promptPaywall({
                    label: 'Upgrade account',
                    description: `Generator form: ${generatorId}`,
                  });
                  return;
                } else {
                  handleSubmit();
                }
              },
              isTrusted: e.isTrusted,
            });
          }}
          disabled={!isFormEnabled}
        >
          Customize now
        </CreateButton>
      )}
    </FormContainer>
  );
};

export const GeneratorFormV2 = ({
  promptInputs,
  handleInputChange,
  handleMultipleInputsChange,
  handleSubmit,
  isLoading,
  isError,
  error,
  formState,
  promptInputSuggestions,
  isPremiumGenerator,
  generatorPlan,
  generatorId,
  upgradeLocation,
  preFillSearch,
  preFillGrade,
}: {
  promptInputs: PromptInput[];
  promptInputSuggestions?: PromptInput[][];
  handleInputChange: (
    inputId: string,
    value: string | number | { filePath: string; fileType?: string },
  ) => void;
  handleMultipleInputsChange?: (
    inputsToChange: {
      inputId: string;
      value: string | number | { filePath: string; fileType: string };
    }[],
  ) => void;
  handleSubmit: () => void;
  isLoading?: boolean;
  isError?: boolean;
  error?: any;
  formState?: GeneratorFormState;
  isPremiumGenerator?: boolean | null;
  generatorPlan?: GeneratorCoarseLicenseLevel | null;
  generatorId?: string;
  upgradeLocation?: string;
  preFillSearch?: string;
  preFillGrade?: string;
}) => {
  const [showErrorModal, setShowErrorModal] = useState(false);
  const { showNewMessages } = useIntercom();
  const { userAccessBadge, isReady, promptPaywall, paywallComponent } = usePaywallPrompt({
    startState: { screen: 'schoolPlanPaywall' },
  });

  const { authGuardComponent, showAuthGuard } = useAuthGuardPrompt(
    undefined,
    undefined,
    generatorId,
  );
  const requiredInputs = useMemo(() => promptInputs.filter(p => p.isRequired), [
    promptInputs,
  ]);

  const optionalInputs = useMemo(() => promptInputs.filter(p => !p.isRequired), [
    promptInputs,
  ]);
  const currentPromptInputSuggestions = usePromptInputSuggestions({
    inputSuggestions: promptInputSuggestions,
    formState,
    promptInputs,
  });

  const [isFormErrorVisible, setIsFormErrorVisible] = useState(false);

  useEffect(() => {
    if (isError) {
      setShowErrorModal(true);
    }
  }, [isError]);

  //Pre fill form with search and or grade
  useEffect(() => {
    if (preFillSearch && !preFillGrade) {
      const topicInput = promptInputs.find(p => p.label.includes('topic'));

      if (topicInput) {
        handleInputChange(topicInput.id, preFillSearch);
      }

      return;
    }
    if (!preFillSearch && preFillGrade) {
      const gradeInput = promptInputs.find(p => p.id.includes('grade'));
      if (gradeInput) {
        handleInputChange(gradeInput.id, preFillGrade);
      }

      return;
    }

    if (preFillSearch && preFillGrade) {
      const topicInput = promptInputs.find(p => p.label.includes('topic'));
      const gradeInput = promptInputs.find(p => p.id.includes('grade'));

      if (topicInput && gradeInput && handleMultipleInputsChange) {
        handleMultipleInputsChange([
          { inputId: topicInput.id, value: preFillSearch },
          { inputId: gradeInput.id, value: preFillGrade },
        ]);
      }
      return;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preFillSearch, preFillGrade]);

  const isFormEnabled = useMemo(() => {
    if (formState) {
      return requiredInputs.every(input => {
        const realValue = formState?.[input.id];
        return realValue && realValue !== '';
      });
    }
    return false;
  }, [requiredInputs, formState]);

  return (
    <FormContainer>
      {paywallComponent}
      {authGuardComponent}
      <BaseModal open={showErrorModal} onCancel={() => setShowErrorModal(false)}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
          }}
        >
          <IconWrapper height="120px" width="120px" style={{ marginTop: 20 }}>
            <FoldedHandsSVG />
          </IconWrapper>

          <ErrorMessage style={{ fontWeight: 800 }}>
            Uh-oh! Something went wrong on our end
          </ErrorMessage>

          <ErrorMessage style={{ fontSize: '16px' }}>
            {error?.customMessage ||
              'Please try again or contact support if the issue persists'}
          </ErrorMessage>
          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <Button width="120px" onClick={() => setShowErrorModal(false)}>
              Ok
            </Button>
            <Button
              width="120px"
              onClick={() => {
                const errorMessage = error?.customMessage || 'Unknown error occurred';
                showNewMessages(
                  `I'm having issues with lesson generation and need help.\n\nError: ${errorMessage}`,
                );
                setShowErrorModal(false);
              }}
            >
              Get Help
            </Button>
          </div>
        </div>
      </BaseModal>
      <GridContainerV2>
        {requiredInputs?.map(promptInput => (
          <InputElementV2
            key={promptInput.id}
            promptInput={promptInput}
            promptInputSuggestions={currentPromptInputSuggestions}
            value={formState?.[promptInput.id]}
            onChange={val => handleInputChange(promptInput.id, val)}
          />
        ))}

        {optionalInputs.length > 0 && (
          <OptionalInputCollapseV2 ghost style={{ width: '100%' }}>
            <OptionalInputCollapsePanel
              header={'Optional inputs'}
              key="optional"
              style={{ color: theme.colors.black, fontWeight: 'bold' }}
            >
              <GridContainer>
                {optionalInputs?.map(promptInput => (
                  <InputElementV2
                    key={promptInput.id}
                    value={formState?.[promptInput.id]}
                    promptInputSuggestions={currentPromptInputSuggestions}
                    promptInput={promptInput}
                    onChange={val => handleInputChange(promptInput.id, val)}
                  />
                ))}
              </GridContainer>
            </OptionalInputCollapsePanel>
          </OptionalInputCollapseV2>
        )}
      </GridContainerV2>
      {isFormErrorVisible && !isFormEnabled && (
        <FormErrorText>Please complete the form</FormErrorText>
      )}
      {isReady &&
      generatorPlan === GeneratorCoarseLicenseLevel.enterprise &&
      userAccessBadge?.license.coarseLevel !== LicenseCoarseLevels.enterprise ? (
        <CreateButtonV2
          type="primary"
          loading={isLoading}
          onClick={e => {
            debounceEventIfNotTrusted({
              cb: () => {
                if (!isFormEnabled) {
                  setIsFormErrorVisible(true);
                  return;
                }

                if (!isReady) {
                  showAuthGuard();
                  return;
                }
                if (
                  generatorPlan === GeneratorCoarseLicenseLevel.enterprise &&
                  userAccessBadge?.license.coarseLevel !== LicenseCoarseLevels.enterprise
                ) {
                  promptPaywall({
                    label: 'Upgrade account',
                    description: `Generator form: ${generatorId}`,
                  });
                  return;
                } else {
                  handleSubmit();
                }
              },
              isTrusted: e.isTrusted,
            });
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: 10,
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <IconWrapper height="30px" width="30px">
              <CuripodAIStarsSvg />
            </IconWrapper>

            <span>Generate Lesson</span>
          </div>
        </CreateButtonV2>
      ) : (
        <CreateButtonV2
          type="primary"
          loading={isLoading}
          onClick={e => {
            debounceEventIfNotTrusted({
              cb: () => {
                if (!isFormEnabled) {
                  setIsFormErrorVisible(true);
                  return;
                }

                if (!isReady) {
                  showAuthGuard();
                  return;
                }
                if (
                  isPremiumGenerator &&
                  userAccessBadge?.license.coarseLevel !== LicenseCoarseLevels.premium &&
                  userAccessBadge?.license.coarseLevel !== LicenseCoarseLevels.enterprise
                ) {
                  promptPaywall({
                    label: 'Upgrade account',
                    description: `Generator form: ${generatorId}`,
                  });
                  return;
                } else {
                  handleSubmit();
                }
              },
              isTrusted: e.isTrusted,
            });
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: 10,
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <IconWrapper height="30px" width="30px">
              <CuripodAIStarsSvg />
            </IconWrapper>

            <span>Generate Lesson</span>
          </div>
        </CreateButtonV2>
      )}
    </FormContainer>
  );
};

const CreateButton = styled(Button)<{ $color?: string }>`
  flex-direction: row-reverse;
  margin: 10px auto 0px auto;
  svg {
    height: 20px;
    width: 20px;
    margin-left: 0px;
    margin-right: 0px;
  }
  @media (max-width: 600px) {
    margin: 10px auto;
  }

  ${({ $color }) =>
    $color &&
    `
    background-color: ${$color};

    &:hover {
      background-color: ${$color};
    }

    &:focus {
      background-color: ${$color};
    }
  `}
`;

const CreateButtonV2 = styled(Button)<{ $color?: string }>`
  gap: 10px;
  align-self: start;
  margin: 40px auto 0px 0px;
  svg {
    height: 30px;
    width: 30px;
    margin-right: 0px;
  }
  min-width: 200px;
  min-height: 60px;
  font-size: 22px;
  font-weight: bold;
  @media (max-width: 767px) {
    margin: 40px auto;
  }
  color: black;
  background-color: #f99746 !important;
  color: white !important;
`;

const GridContainer = styled.div`
  display: grid;
  width: 100%;
  gap: 14px;
  grid-template-columns: 1fr;
`;

const FormErrorText = styled.div`
  color: red;
  font-size: 20px;
  text-align: center;
  margin: 20px 0;
`;

const GridContainerV2 = styled.div`
  display: grid;
  width: 100%;
  gap: 30px;
  grid-template-columns: 1fr;
`;

const OptionalInputCollapsePanel = styled(Collapse.Panel)`
  background-color: transparent;
  margin-top: 10px;
`;

const OptionalInputCollapse = styled(Collapse)`
  background-color: transparent;
  .ant-collapse-header {
    padding: 0 !important;
  }
  .ant-collapse-content-box {
    padding: 0;
  }
`;

const OptionalInputCollapseV2 = styled(Collapse)`
  background-color: transparent;
  .ant-collapse-header {
    padding: 0 !important;
    color: white !important;
  }
  .ant-collapse-content-box {
    padding: 0;
  }
`;

export const useGeneratorFormState = (
  promptInputs?: PromptInput[],
  existingFormState?: GeneratorFormState,
) => {
  const [formState, setFormState] = useState<GeneratorFormState>(
    promptInputs
      ? generateFormState(promptInputs)
      : existingFormState
      ? existingFormState
      : {},
  );

  useEffect(() => {
    if (existingFormState) {
      setFormState(existingFormState);
    }
  }, [existingFormState]);

  const handleInputChange = useCallback(
    (
      inputId: string,
      value: string | number | { filePath: string; fileType?: string },
    ) => {
      if (typeof value === 'string' || typeof value === 'number') {
        setFormState({
          ...formState,
          [inputId]: value,
        });
        return;
      }
      setFormState({
        ...formState,
        [inputId]: value,
      });
    },
    [formState],
  );

  const handleMultipleInputsChange = useCallback(
    (
      inputsToChange: {
        inputId: string;
        value: string | number | { filePath: string; fileType: string };
      }[],
    ) => {
      const newFormState = { ...formState };
      inputsToChange.forEach(({ inputId, value }) => {
        if (typeof value === 'string' || typeof value === 'number') {
          newFormState[inputId] = value;
          return;
        }
        newFormState[inputId] = value;
      });
      setFormState(newFormState);
    },
    [formState],
  );

  return {
    handleInputChange,
    handleMultipleInputsChange,
    formState,
  };
};

const ErrorMessage = styled.h4`
  font-weight: bold;
  width: 100%;
  text-align: center;
  margin-top: 20px;
  font-size: 18px;

  p {
    margin-top: 10px;
    font-size: 14px;
  }
`;

export default GeneratorForm;

const FormContainer = styled.div``;

const FileUploadArea = ({
  uploadedFile,
  setUploadedFile,
  isUploadingFile,
  isErrorUploadingFile,
  handleFileSelected,
  resetFileUpload,
  fileSizeExceeded,
  label = 'Upload Slides',
}: {
  uploadedFile: { filePath: string; fileType: string } | undefined;
  setUploadedFile: (
    uploadedFile:
      | {
          filePath: string;
          fileType: string;
        }
      | undefined,
  ) => void;
  isUploadingFile: boolean;
  isErrorUploadingFile: boolean;
  handleFileSelected: (file: File) => void;
  resetFileUpload: () => void;
  fileSizeExceeded: boolean;
  label?: string;
}) => {
  const { t } = useTranslate();
  const [fileName, setFileName] = useState<string | undefined>();

  useEffect(() => {
    if (uploadedFile) {
      //Cut off the first section of the file path to give the name of the file
      const index = uploadedFile.filePath.indexOf('_');

      if (index !== -1) {
        const findFileName = uploadedFile.filePath?.substring(index + 1);
        setFileName(findFileName);
      }
    }
  }, [uploadedFile]);

  return (
    <>
      {!(isUploadingFile || uploadedFile || isErrorUploadingFile) && (
        <div
          style={{
            width: '100%',
          }}
        >
          <p style={{ fontSize: 12, marginBottom: 4 }}>{label}</p>

          {fileSizeExceeded && (
            <ErrorMessage style={{ fontSize: 12, textAlign: 'left' }}>
              {t(
                'fileSizeExceeded',
                'Oops looks like this presentation is too big. Please try uploading a file smaller than 100MB',
              )}
            </ErrorMessage>
          )}
          <PresentationFileSelector onFileSelected={handleFileSelected} />
        </div>
      )}
      {(isUploadingFile || uploadedFile || isErrorUploadingFile) && (
        <div
          style={{
            alignItems: 'space-beetween',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {isUploadingFile && (
            <div
              style={{
                gridColumn: '1/3',
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Loader />
              <h3 style={{ width: '100%', textAlign: 'center' }}>{t('Uploading')}</h3>
            </div>
          )}
          {isErrorUploadingFile && (
            <div
              style={{
                width: '100%',
              }}
            >
              <IconWrapper height="100px" width="100px">
                <FoldedHandsSVG />
              </IconWrapper>
              <h3 style={{ width: '100%', textAlign: 'center' }}>
                {t('Failed to upload')}
              </h3>
              <p style={{ textAlign: 'center' }}>
                {t(
                  'Make sure the file is correct format, not corrupted and there is no password protection',
                )}
              </p>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <Button
                  type="primary"
                  onClick={() => {
                    resetFileUpload();
                    setUploadedFile(undefined);
                  }}
                >
                  {t('Please try again')}
                </Button>
              </div>
            </div>
          )}
          {uploadedFile && (
            <div
              style={{
                width: '100%',
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 5,
                alignItems: 'center',
              }}
            >
              <h3 style={{ margin: 0, width: '100%', wordBreak: 'break-all' }}>
                {fileName}{' '}
                <span style={{ fontSize: 10, display: 'block' }}>
                  {t('successfully uploaded')}
                </span>
              </h3>
              <Button type="ghost" onClick={() => setUploadedFile(undefined)}>
                {t('Change')}
              </Button>
            </div>
          )}
        </div>
      )}
    </>
  );
};

const FileUploadAreaV2 = ({
  uploadedFile,
  setUploadedFile,
  isUploadingFile,
  isErrorUploadingFile,
  handleFileSelected,
  resetFileUpload,
  fileSizeExceeded,
  label = 'Upload Slides',
}: {
  uploadedFile: { filePath: string; fileType: string } | undefined;
  setUploadedFile: (
    uploadedFile:
      | {
          filePath: string;
          fileType: string;
        }
      | undefined,
  ) => void;
  isUploadingFile: boolean;
  isErrorUploadingFile: boolean;
  handleFileSelected: (file: File) => void;
  resetFileUpload: () => void;
  fileSizeExceeded: boolean;
  label?: string;
}) => {
  const { t } = useTranslate();
  const [fileName, setFileName] = useState<string | undefined>();

  useEffect(() => {
    if (uploadedFile) {
      //Cut off the first section of the file path to give the name of the file
      const index = uploadedFile.filePath?.indexOf('_');

      if (index !== -1) {
        const findFileName = uploadedFile.filePath.substring(index + 1);
        setFileName(findFileName);
      }
    }
  }, [uploadedFile]);

  return (
    <>
      {!(isUploadingFile || uploadedFile || isErrorUploadingFile) && (
        <div
          style={{
            width: '100%',
          }}
        >
          <p style={{ fontSize: 18, marginBottom: 4, color: 'white', fontWeight: 800 }}>
            {label}
          </p>

          {fileSizeExceeded && (
            <ErrorMessage style={{ fontSize: 12, textAlign: 'left' }}>
              {t(
                'fileSizeExceeded',
                'Oops looks like this presentation is too big. Please try uploading a file smaller than 100MB',
              )}
            </ErrorMessage>
          )}
          <PresentationFileSelectorV2 onFileSelected={handleFileSelected} />
        </div>
      )}
      {(isUploadingFile || uploadedFile || isErrorUploadingFile) && (
        <div
          style={{
            alignItems: 'space-beetween',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {isUploadingFile && (
            <div
              style={{
                gridColumn: '1/3',
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Loader />
              <h3 style={{ width: '100%', textAlign: 'center', color: 'white' }}>
                {t('Uploading')}
              </h3>
            </div>
          )}
          {isErrorUploadingFile && (
            <div
              style={{
                width: '100%',
              }}
            >
              <IconWrapper height="100px" width="100px">
                <FoldedHandsSVG />
              </IconWrapper>
              <h3 style={{ width: '100%', textAlign: 'center' }}>
                {t('Failed to upload')}
              </h3>
              <p style={{ textAlign: 'center' }}>
                {t(
                  'Make sure the file is correct format, not corrupted and there is no password protection',
                )}
              </p>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <Button
                  type="primary"
                  onClick={() => {
                    resetFileUpload();
                    setUploadedFile(undefined);
                  }}
                >
                  {t('Please try again')}
                </Button>
              </div>
            </div>
          )}
          {uploadedFile && (
            <div
              style={{
                width: '100%',
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 5,
                alignItems: 'center',
              }}
            >
              <h3
                style={{
                  margin: 0,
                  width: '100%',
                  wordBreak: 'break-all',
                  color: 'white',
                }}
              >
                {fileName}{' '}
                <span style={{ fontSize: 10, display: 'block' }}>
                  {t('successfully uploaded')}
                </span>
              </h3>
              <Button type="ghost" onClick={() => setUploadedFile(undefined)}>
                {t('Change')}
              </Button>
            </div>
          )}
        </div>
      )}
    </>
  );
};

const useSinglePromptinputSuggestion = ({
  promptInput,
  promptInputSuggestions = [],
  value,
}: {
  promptInput: PromptInput;
  promptInputSuggestions?: PromptInput[];
  value?: string | number | { filePath: string; fileType?: string };
}) => {
  const [suggestionToRender, setSuggestionToRender] = useState<string | undefined>(
    undefined,
  );
  const intervalRef = useRef<NodeJS.Timeout>();
  const [renderedSuggestion, setRenderedSuggestion] = useState<string>('');
  const intervalTime = useMemo(() => {
    if (suggestionToRender && suggestionToRender?.length > 10) {
      return Math.max(1000 / suggestionToRender.length, 10);
    }
    return 100;
  }, [suggestionToRender]);

  // when the prompt input changes, we need to change the suggestion
  useEffect(() => {
    // if the prompt input is not short or long text, we don't need to render the placeholder
    if (promptInput.type !== 'shortText' && promptInput.type !== 'longText') return;

    // if the prompt input has a value, we don't need to render the placeholder
    if (value) return;

    const newSuggestion = promptInputSuggestions?.find(p => p.id === promptInput.id);
    if (newSuggestion) {
      setRenderedSuggestion('');
      setSuggestionToRender(newSuggestion.value);
      return;
    }

    // if there is no suggestion, we remove the placeholder
    if (promptInputSuggestions && promptInputSuggestions.length > 0) {
      setRenderedSuggestion('');
      setSuggestionToRender(undefined);
      return;
    }
  }, [promptInput, promptInputSuggestions, value]);

  // when the suggestion changes, we need to create the dynamic writing experience and thus render the placeholder
  useEffect(() => {
    if (intervalRef?.current) {
      clearInterval(intervalRef.current);
    }
    intervalRef.current = setInterval(() => {
      // this logic makes the rendered placeholder string look likes its being typed
      if (suggestionToRender) {
        setRenderedSuggestion(e => {
          return suggestionToRender?.slice(0, e.length + 1);
        });
      }
    }, intervalTime);

    return () => {
      if (intervalRef?.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [intervalTime, renderedSuggestion, suggestionToRender]);
  return renderedSuggestion;
};

const usePromptInputSuggestions = ({
  inputSuggestions,
  formState,
  promptInputs,
}: {
  inputSuggestions?: PromptInput[][];
  formState: GeneratorFormState | undefined;
  promptInputs: PromptInput[];
}) => {
  /**
   * used for finding the current input suggestion to render
   * */

  const intervalRef = useRef<NodeJS.Timeout>();
  const [selectedInputSuggestionIndex, setSelectedInputSuggestionIndex] = useState(0);
  // makes a interval that changes the placeholder every 4 seconds
  useEffect(() => {
    if (!inputSuggestions) return;

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // if we have filled in all the text inputs, we don't need to render the placeholder
    if (formState) {
      const textPromptInputs = promptInputs.filter(
        e => e.type === 'shortText' || e.type === 'longText',
      );
      if (textPromptInputs.every(p => formState[p.id])) {
        return;
      }
    }

    intervalRef.current = setInterval(() => {
      setSelectedInputSuggestionIndex(i => (i + 1) % inputSuggestions.length);
    }, 3000);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [selectedInputSuggestionIndex, inputSuggestions, formState, promptInputs]);

  const currentInputSuggestions = useMemo(() => {
    if (!inputSuggestions) return [];
    return inputSuggestions[selectedInputSuggestionIndex];
  }, [inputSuggestions, selectedInputSuggestionIndex]);

  return currentInputSuggestions;
};
